import React from 'react';
import { Button, Caption1, Checkbox, Field, Input, makeStyles, Text, tokens } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import type { ResourcePoolObject } from '../../types/trapi';
import { OwnershipSection } from './SharedFields';

const useStyles = makeStyles({
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  focusableButton: {
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusMedium,
    },
  },
});

export interface ResourcePoolFormProps {
  mode: 'create' | 'edit';
  initialData?: ResourcePoolObject;
  existingNames?: string[];
  backendOptions: string[];
  excludedBackends?: string[];
  isLoading?: boolean;
  onSubmit: (data: Omit<ResourcePoolObject, 'id' | 'metadata'>) => void;
  onCancel: () => void;
  formId?: string;
  hideActions?: boolean;
}

type FormData = {
  name: string;
  description: string;
  backends: string[];
  ownership: { owner: string; team: string; contact: string };
};

type FormErrors = Partial<Record<'name' | 'backends', string>>;

// ── Collapsible backend checkbox list for resource pools ──
function BackendsCheckboxSection({ selected, backendOptions, excludedBackends = [], error, onChange }: {
  selected: string[];
  backendOptions: string[];
  excludedBackends?: string[];
  error?: string;
  onChange: (backends: string[]) => void;
}) {
  const styles = useStyles();
  const [open, setOpen] = React.useState(true);
  const [filter, setFilter] = React.useState('');
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const filtered = React.useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return backendOptions;
    return backendOptions.filter(b => b.toLowerCase().includes(q));
  }, [backendOptions, filter]);

  const toggle = (name: string) => {
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  };

  return (
    <section style={{ display: 'grid', gap: 4, paddingTop: 4, borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none', outline: 'none' }}
        className={styles.focusableButton}
        onClick={() => setOpen(p => !p)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOpen(p => !p); }}
      >
        {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        <Text weight="semibold">Backends</Text>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
          {selected.length} selected
        </Caption1>
      </div>
      {error && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Caption1>}
      {open && (
        <>
          <Input
            size="small"
            placeholder="Filter backends…"
            value={filter}
            onChange={(_, d) => setFilter(d.value)}
            style={{ marginTop: 4 }}
          />
          <div style={{
            maxHeight: 240, overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke3}`,
            borderRadius: tokens.borderRadiusMedium, padding: 4, marginTop: 2,
          }}>
            {backendOptions.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No backends available</Caption1>
            ) : filtered.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No backends match filter</Caption1>
            ) : (
              filtered.map(name => (
                <Checkbox
                  key={name}
                  checked={selectedSet.has(name)}
                  onChange={() => toggle(name)}
                  label={name}
                  style={{ display: 'flex', width: '100%' }}
                />
              ))
            )}
          </div>
          {excludedBackends.length > 0 && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {excludedBackends.length} backend{excludedBackends.length === 1 ? '' : 's'} unavailable (assigned to another pool)
            </Caption1>
          )}
        </>
      )}
    </section>
  );
}

const ResourcePoolForm: React.FC<ResourcePoolFormProps> = ({
  mode,
  initialData,
  existingNames = [],
  backendOptions,
  isLoading,
  excludedBackends = [],
  onSubmit,
  onCancel,
  formId,
  hideActions = false,
}) => {
  const styles = useStyles();
  const [data, setData] = React.useState<FormData>({
    name: '',
    description: '',
    backends: [],
    ownership: { owner: '', team: '', contact: '' },
  });
  const [errors, setErrors] = React.useState<FormErrors>({});

  React.useEffect(() => {
    if (initialData) {
      setData({
        name: initialData.name || '',
        description: initialData.description || '',
        backends: initialData.backends || [],
        ownership: {
          owner: initialData.ownership?.owner || '',
          team: initialData.ownership?.team || '',
          contact: initialData.ownership?.contact || '',
        },
      });
    }
  }, [initialData]);

  const validate = () => {
    const e: FormErrors = {};
    if (!data.name.trim()) e.name = 'Name is required';
    else if (mode === 'create' && existingNames.map(n => n.toLowerCase()).includes(data.name.toLowerCase())) e.name = 'Name must be unique';
    if (!Array.isArray(data.backends) || data.backends.length === 0) e.backends = 'Select at least one backend';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload: Omit<ResourcePoolObject, 'id' | 'metadata'> = {
      name: data.name.trim(),
  resourcepool: data.name.trim(),
      description: data.description.trim(),
      backends: data.backends,
      ownership: data.ownership,
      ci: 'RESOURCEPOOL',
    };
    onSubmit(payload);
  };

  return (
    <form noValidate id={formId} onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
      {/* Basic Information */}
      <section style={{ display: 'grid', gap: 8 }}>
        <Text weight="semibold">Basic Information</Text>
        <Field label="Name" required validationMessage={errors.name}>
          <Input value={data.name} onChange={(_, d) => setData(p => ({ ...p, name: d.value }))} />
        </Field>
        <Field label="Description">
          <Input value={data.description} onChange={(_, d) => setData(p => ({ ...p, description: d.value }))} />
        </Field>
      </section>

      {/* Ownership */}
      <OwnershipSection
        data={data.ownership}
        onChange={(ownership) => setData(p => ({ ...p, ownership }))}
      />

      {/* Backends — collapsible checkbox list */}
      <BackendsCheckboxSection
        selected={data.backends}
        backendOptions={backendOptions}
        excludedBackends={excludedBackends}
        error={errors.backends}
        onChange={(backends) => setData(p => ({ ...p, backends }))}
      />

      {!hideActions && (
        <div className={styles.formActions}>
          <Button appearance="outline" type="button" onClick={onCancel} disabled={!!isLoading}>Cancel</Button>
          <Button appearance="primary" type="submit" disabled={!!isLoading}>{isLoading ? 'Saving…' : mode === 'create' ? 'Create Pool' : 'Update Pool'}</Button>
        </div>
      )}
    </form>
  );
};

export default ResourcePoolForm;
